/**
 * RP4 — Selection + hover texture sync helpers extracted from
 * `Scene3DStage.tsx`.
 *
 * Q18.B's per-bucket `SelectionStateTexture` carries a single byte per
 * entity (selected / hovered / faded / hidden bits). Two distinct
 * effects keep that byte in sync with the App-level state:
 *
 *   1. **Selection sync** — runs whenever `selectedEntityIds` changes.
 *      Each call writes only the changed bytes (per-bucket diff).
 *
 *   2. **Hover bridge** — subscribes to the standalone
 *      `useStageHoverStore` so cursor moves don't re-render the whole
 *      Scene3DStage tree. Multi-bucket coordination: the previous
 *      hover bucket gets cleared every step.
 *
 * # Why a dedicated module
 *
 * The two effects share the slot/texture map traversal, so co-locating
 * the helpers gives the orchestrator a single import per concern and
 * keeps the closure capture tidy.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx` lines
 * 2228-2281.
 */

import type { MeshKeyId } from "../batchedLayer";
import { useStageHoverStore } from "../../state/hoverState";

import type { BucketState, EntityRender } from "./SceneRefsTypes";

/** Shared empty slot list — a bucket with no hovered slot is cleared with it. */
const EMPTY_SLOTS: readonly number[] = [];

/**
 * Push a slot's `instanceIndex` into a per-bucket index array (creating the
 * array on first write). Shared by BOTH the selection fan-out
 * (`applySelectionToTextures`) and the hover fan-out (`subscribeHoverHighlight`)
 * so a block-content satellite sharing the parent's bucket contributes an
 * ADDITIONAL slot in that bucket rather than overwriting the parent's — the
 * one-truth that makes same-bucket satellites light up (jscpd one-truth /
 * S9b round-5 P3).
 */
function pushSlotIndex(
  byBucket: Map<MeshKeyId, number[]>,
  slot: { keyId: MeshKeyId; instanceIndex: number },
): void {
  const arr = byBucket.get(slot.keyId);
  if (arr) arr.push(slot.instanceIndex);
  else byBucket.set(slot.keyId, [slot.instanceIndex]);
}

interface SelectionSyncArgs {
  /**
   * Opt 8 — consolidated per-entity render Map. The `textureSlot`
   * field on each entry replaces the previous `entityToTextureSlot`
   * Map.
   */
  entityRenders: Map<string, EntityRender>;
  /**
   * Opt 8 — consolidated per-bucket render Map. The `selectionTexture`
   * field on each entry replaces the previous `selectionTextures` Map.
   */
  buckets: Map<MeshKeyId, BucketState>;
  selectedEntityIds: readonly string[] | undefined;
}

/**
 * Q18.B selection-sync pass. Diffs selected ids onto the per-bucket
 * texture; returns `true` when at least one texture exists (so the
 * caller knows to `invalidate()`).
 */
export function applySelectionToTextures(args: SelectionSyncArgs): boolean {
  if (args.buckets.size === 0) return false;
  // Group selected ids by bucket keyId for batched per-texture diff.
  const bySelector = new Map<MeshKeyId, number[]>();
  for (const id of args.selectedEntityIds ?? []) {
    const render = args.entityRenders.get(id);
    if (!render) continue;
    if (render.textureSlot) pushSlotIndex(bySelector, render.textureSlot);
    // S9b (Codex P2) — fan the parent's highlight out to its render-only
    // block-content satellites so the whole MLEADER callout lights up, not
    // just the leader-line slot. Empty for non-MLEADER entities.
    if (render.satelliteSlots) {
      for (const s of render.satelliteSlots) pushSlotIndex(bySelector, s);
    }
  }
  for (const [keyId, bucket] of args.buckets) {
    const slots = bySelector.get(keyId) ?? [];
    bucket.selectionTexture.setSelectedIds(slots);
  }
  return true;
}

interface HoverSyncArgs {
  entityRenders: Map<string, EntityRender>;
  buckets: Map<MeshKeyId, BucketState>;
  /** The currently-hovered entity id, or `null` when nothing is hovered. */
  hoveredEntityId: string | null;
}

/**
 * One-truth hover fan-out: stamp the hovered entity's OWN slot AND its
 * block-content satellite slots onto every bucket's selection-state texture
 * (clearing buckets that own no hovered slot). Extracted so BOTH the hover
 * subscription (`subscribeHoverHighlight`) and the per-frame relift path
 * (`FrameLoops` — S9b, Codex round-8 P2 "Reapply highlight state after
 * satellite relift") re-apply the hover through the SAME slot-fan logic; a
 * relift replaces the satellites' instance indices, so the hover must be
 * re-stamped onto the FRESH slots or the callout stops previewing until the
 * cursor moves. No-op when no bucket has been keyed yet.
 */
export function applyHoverToTextures(args: HoverSyncArgs): void {
  if (args.buckets.size === 0) return;
  const hoverByBucket = new Map<MeshKeyId, number[]>();
  const render = args.hoveredEntityId
    ? args.entityRenders.get(args.hoveredEntityId)
    : undefined;
  if (render?.textureSlot) pushSlotIndex(hoverByBucket, render.textureSlot);
  if (render?.satelliteSlots) {
    for (const s of render.satelliteSlots) pushSlotIndex(hoverByBucket, s);
  }
  for (const [keyId, bucket] of args.buckets) {
    bucket.selectionTexture.setHoveredIds(
      hoverByBucket.get(keyId) ?? EMPTY_SLOTS,
    );
  }
}

/**
 * ONE-TRUTH re-stamp of the whole per-instance highlight byte (selection AND
 * hover) onto the buckets' CURRENT slots.
 *
 * `SelectionStateTexture` is a DERIVED cache: its bytes are the only record of
 * "which slot is selected / hovered", and that record is destroyed whenever the
 * slots are re-indexed. Two paths do exactly that:
 *
 *   * `finishSceneBulkLoad` — `disposeSceneBuckets` disposes every
 *     `SelectionStateTexture` and clears `entityRenders`, then
 *     `buildBucketMeshes` allocates FRESH all-zero ("normal") textures;
 *   * `FrameLoops`' viewport-cull relift — `BatchedLayer.remove/addEntity`
 *     hands the touched entities NEW `instanceIndex` values.
 *
 * Neither is triggered by a selection or hover CHANGE, and the two standalone
 * sync paths (`useSceneEffects` effect 3 / `subscribeHoverHighlight`) only fire
 * ON such a change — so without an explicit re-derivation the highlight is
 * silently dropped and nothing ever puts it back (a repeat of the SAME
 * selection produces no identity change either, so it cannot repair it).
 *
 * Selection is read from the caller's live snapshot; hover is read from the
 * standalone `useStageHoverStore` (the same source `subscribeHoverHighlight`
 * subscribes to), so both bytes come from their authoritative owner rather than
 * from a renderer-side copy.
 */
export function restampHighlightState(args: SelectionSyncArgs): void {
  applySelectionToTextures(args);
  applyHoverToTextures({
    entityRenders: args.entityRenders,
    buckets: args.buckets,
    hoveredEntityId: useStageHoverStore.getState().entityId,
  });
}

interface HoverSubscriptionArgs {
  entityRenders: Map<string, EntityRender>;
  buckets: Map<MeshKeyId, BucketState>;
  invalidate: () => void;
}

/**
 * AutoCAD SELECTIONPREVIEW hover-highlight bridge — subscribe to the
 * standalone hover store so cursor moves don't re-render the whole
 * Scene3DStage tree (the selection-state texture is mutated in-place
 * and only `invalidate()` is needed to repaint).
 *
 * # Multi-bucket coordination
 *
 * The selection texture lives one-per-bucket. setHoveredId on a
 * bucket only knows about its own slots, so we explicitly clear the
 * hover on every bucket that currently shows a hover then set it on
 * the bucket that owns the new entity. This avoids a stale hover
 * glyph staying visible on a previously-hovered bucket.
 *
 * Returns the unsubscribe function (caller stuffs it in the
 * `useEffect` cleanup return).
 */
export function subscribeHoverHighlight(
  args: HoverSubscriptionArgs,
): () => void {
  // The earlier `if (selectionTextures.size === 0) return no-op` early
  // exit froze hover-highlight when this effect ran before
  // `SceneBulkLoad` populated the textures (mount-time: bulk-load
  // hadn't yet keyed any bucket → size 0 → unsubscribed forever even
  // after import filled the maps). The closure captures the Map refs
  // by reference, so subscribing immediately and letting `apply` no-op
  // on empty maps lets later inserts light up without a re-subscribe.
  const apply = (entityId: string | null): void => {
    if (args.buckets.size === 0) return;
    // S9b (Codex round-5 P3 — "Highlight same-bucket MLEADER satellites on
    // hover"): hover fans out to the entity's OWN slot AND its block-content
    // satellite slots via the shared `applyHoverToTextures` one-truth (a
    // satellite re-homed onto the parent's layer/color often shares the
    // parent's BUCKET, so ALL slots per bucket are collected and stamped via
    // `setHoveredIds` plural rather than a first-write-wins single slot).
    applyHoverToTextures({
      entityRenders: args.entityRenders,
      buckets: args.buckets,
      hoveredEntityId: entityId,
    });
    args.invalidate();
  };
  apply(useStageHoverStore.getState().entityId);
  const unsub = useStageHoverStore.subscribe((state, prev) => {
    if (state.entityId === prev.entityId) return;
    apply(state.entityId);
  });
  return unsub;
}
